// frontend/src/services/socket.js
// Responsibility:
// - Central Socket.IO client
// - UPS admin refetch logic (existing, unchanged in spirit)
// - Agency refetch logic (invalidate-only, centralized, debounced)
//
// Phase 8.7C+
// - Socket events invalidate UI
// - Components refetch read-models when statsRefreshKey changes
// - Stats refreshKey is bumped once per consolidated socket burst
//
// ✅ Controlled init:
// - initSocket() creates + wires handlers once (singleton)
// - getSocket() returns the instance (or null if not initialized)
//
// ✅ Fix 1: trailing debounce (reset timer on every event)
// ✅ Fix 2: ignore agency invalidations briefly during startup (guard set immediately + on connect)

import { io } from 'socket.io-client';
import store from '../store';

// Entity cache (details modals, photos, etc.)
import { upsertDriver } from '../features/drivers/driverSlice';

// UPS-specific reads
import { loadSiteAssignments } from '../features/assignments/assignmentsSlice';
import { loadBannedDrivers } from '../features/bans/bansSlice';

// Shared UI invalidation
import { bumpStatsRefreshKey } from '../features/ui/filtersSlice';

let socket = null;
const isSocketDebugEnabled = process.env.NODE_ENV !== 'production';

function logSocketDebug(...args) {
  if (!isSocketDebugEnabled) return;
  // eslint-disable-next-line no-console
  console.log(...args);
}

// ✅ Fix 2: ignore agency invalidations for a short window during startup burst
let agencyIgnoreUntil = 0;

/* ============================================================================
   UPS ADMIN SOCKET HANDLING (existing behavior, preserved)
============================================================================ */

function refetchUpsDriversViewIfRelevant() {
  const p = window.location?.pathname || '';
  if (!p.startsWith('/ups-admin/drivers')) return;

  const state = store.getState();
  const siteId = state.sites?.selectedSiteId;

  // Banned tab
  if (p.startsWith('/ups-admin/drivers/banned')) {
    store.dispatch(loadBannedDrivers());
    store.dispatch(bumpStatsRefreshKey());
    return;
  }

  // Blocked / Orphaned stays on its own read-model.
  // Do not refetch it through assignmentsSlice.
  if (
    p.startsWith('/ups-admin/drivers/blocked') ||
    p.startsWith('/ups-admin/drivers/orphaned')
  ) {
    store.dispatch(bumpStatsRefreshKey());
    return;
  }

  if (!siteId) return;

  // Requests tab
  if (p.startsWith('/ups-admin/drivers/requests')) {
    store.dispatch(loadSiteAssignments({ siteId, statuses: ['requested'] }));
    store.dispatch(bumpStatsRefreshKey());
    return;
  }

  // Reserved tab
  if (p.startsWith('/ups-admin/drivers/reserved')) {
    store.dispatch(loadSiteAssignments({ siteId, statuses: ['confirmed'] }));
    store.dispatch(bumpStatsRefreshKey());
    return;
  }

  // Active / Organized default
  store.dispatch(loadSiteAssignments({ siteId, statuses: ['active'] }));
  store.dispatch(bumpStatsRefreshKey());
}

/* ============================================================================
   AGENCY SOCKET HANDLING (invalidate-only + debounced)
============================================================================ */

let agencyRefetchTimer = null;

// 🔎 DEBUG: added `reason` param + detailed logging to trace startup bumps
function scheduleAgencyRefetchDebounced(reason = 'unknown') {
  const p = window.location?.pathname || '';

  // 🔎 DEBUG LOG — every time an agency invalidation is *considered*
  logSocketDebug('[socket][agency] schedule called', {
    reason,
    path: p,
    connected: !!socket?.connected,
    now: Date.now(),
    ignoreUntil: agencyIgnoreUntil,
    ignored: Date.now() < agencyIgnoreUntil,
  });

  if (!p.startsWith('/agency')) return;

  // 🔎 DEBUG LOG — skip if socket not connected yet
  if (!socket || !socket.connected) {
    logSocketDebug('[socket][agency] skip: socket not connected');
    return;
  }

  // 🔎 DEBUG LOG — skip during startup ignore window
  if (Date.now() < agencyIgnoreUntil) {
    logSocketDebug('[socket][agency] skip: within ignore window');
    return;
  }

  // ✅ Fix 1: trailing debounce (reset timer on every event)
  if (agencyRefetchTimer) {
    logSocketDebug('[socket][agency] debounce: reset timer');
    clearTimeout(agencyRefetchTimer);
  }

  agencyRefetchTimer = setTimeout(() => {
    agencyRefetchTimer = null;

    // 🔎 DEBUG LOG — the actual second-wave trigger
    logSocketDebug('[socket][agency] bumpStatsRefreshKey DISPATCH');
    store.dispatch(bumpStatsRefreshKey());
  }, 350);
}

/* ============================================================================
   CONTROLLED INIT
============================================================================ */

function registerSocketHandlers(s) {
  // Keep driver entity cache fresh
  s.on('driver:photoUpdated', ({ driver }) => {
    if (driver) store.dispatch(upsertDriver(driver));
  });

  s.on('driverUpdated', (payload) => {
    const driver = payload?.driver ?? payload;
    if (driver) store.dispatch(upsertDriver(driver));

    refetchUpsDriversViewIfRelevant();

    // 🔎 DEBUG: pass event name through
    scheduleAgencyRefetchDebounced('driverUpdated');
  });

  // Events that affect lane membership
  [
    'driverAdded',
    'driverDeleted',
    'driverBanned',
    'driverUnbanned',
    'driver:statusChanged',
    'assignmentCreated',
    'assignmentUpdated',
    'assignments:changed',
  ].forEach((evt) => {
    s.on(evt, () => {
      refetchUpsDriversViewIfRelevant();

      // 🔎 DEBUG: pass event name through
      scheduleAgencyRefetchDebounced(evt);
    });
  });
}

export function initSocket() {
  if (socket) return socket; // ✅ singleton

  // 🔎 DEBUG: guard set immediately on init
  agencyIgnoreUntil = Date.now() + 1200;
  logSocketDebug('[socket] init: set agencyIgnoreUntil', {
    agencyIgnoreUntil,
    now: Date.now(),
  });

  socket = io({ path: '/socket.io', transports: ['websocket'] });

  // 🔎 DEBUG: guard re-applied on connect
  socket.on('connect', () => {
    agencyIgnoreUntil = Date.now() + 1200;
    logSocketDebug('[socket] connected', {
      socketId: socket.id,
      agencyIgnoreUntil,
      now: Date.now(),
    });
  });

  registerSocketHandlers(socket);
  return socket;
}

export function getSocket() {
  return socket;
}
